import { type CSSProperties, type MutableRefObject, memo, type ReactNode, useCallback, useEffect, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { List, WindowScroller } from 'react-virtualized';

import { GalleryViewBox } from './GalleryViewBox';
import { type GalleryItem, type GallerySection } from './types';
import { calculateListRowHeight } from './utils/calculateListRowHeight';
import { sectionsToRows } from './utils/sectionsToRows';

const itemRenderer = ({ box, render, index }: GalleryItem, isVisible: boolean) => (
  <GalleryViewBox box={box} index={index} isVisible={isVisible} key={index} render={render} />
);

export type GalleryViewProps = {
  sections: GallerySection[];
  width: number;
  rowRenderer?: (params: {
    index: number;
    style: CSSProperties;
    row: GalleryItem[];
    isVisible: boolean;
    itemRenderer: (item: GalleryItem, isVisible: boolean) => ReactNode;
  }) => ReactNode;
  style?: CSSProperties;
  scrollElementRef: MutableRefObject<HTMLDivElement | HTMLElement | null>;
};

/**
 * This component displays Gallery view
 */
export const GalleryView = memo(({ scrollElementRef, sections, width, style, rowRenderer }: GalleryViewProps) => {
  const rows = sectionsToRows(sections);

  const listRef = useRef<List | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <We want to recompute the row heights when the rows change>
  useEffect(() => {
    listRef.current?.recomputeRowHeights();
  }, [rows]);

  const getRowHeight = useCallback(({ index }: { index: number }) => calculateListRowHeight({ index, rows }), [rows]);

  return scrollElementRef.current ? (
    <WindowScroller scrollElement={scrollElementRef.current}>
      {({ scrollTop, isScrolling }) => (
        <List
          autoHeight
          height={typeof window !== 'undefined' ? window.innerHeight : 0}
          isScrolling={isScrolling}
          overscanRowCount={2}
          ref={listRef}
          rowCount={rows.length}
          rowHeight={getRowHeight}
          rowRenderer={({ index, style, isVisible }) => {
            const row = rows[index];

            if (!row) {
              return <div key={index} style={{ background: 'white', height: 100, width: 100 }} />;
            }

            return rowRenderer ? (
              rowRenderer({ index, isVisible, itemRenderer, row, style })
            ) : (
              <div key={index} style={style}>
                {row.map((item) => itemRenderer(item, isVisible))}
              </div>
            );
          }}
          scrollTop={scrollTop}
          style={style}
          tabIndex={-1}
          width={width}
        />
      )}
    </WindowScroller>
  ) : null;
}, isEqual);

GalleryView.displayName = 'GalleryView';
